/* -*- fill-column: 132; comment-column: 48 -*- */

import { grommet } from 'grommet/themes';
import { deepMerge } from 'grommet/utils';

const fontFamilySans  = `'Work Sans', sans-serif`;
const fontFamilySerif = `'Alice', serif`;
const fontSans = {
  family: fontFamilySans,
};
const fontSerif = {
  family: fontFamilySerif,
};

// new system of theme colors, based on styles from main website
const ACCENT                    = '#446a55';    /* hsl(146.84, 21.84%, 34.12%)   green accent */
const BLACK                     = '#032a42';    /* hsl(202.86, 91.3%, 13.53%)    dark, more saturated  green-blue */
const DARK_ACCENT               = '#1f7cb5';    /* hsl(202.8, 70.75%, 41.57%)    medium, less saturated green-blue */
const LIGHT_ACCENT              = '#6bb18c';    /* hsl(148.29, 30.97%, 55.69%)   green accent, lighter, more saturated */
const SAFE_DARK_ACCENT          = '#446a55';    /* hsl(146.84, 21.84%, 34.12%)   green */
const SAFE_INVERSE_ACCENT       = '#ffffff';    /* hsl(0, 0%, 100%)              pure white */
const SAFE_INVERSE_DARK_ACCENT  = '#ffffff';    /* hsl(0, 0%, 100%)              pure white */
const SAFE_INVERSE_LIGHT_ACCENT = '#ffffff';    /* hsl(0, 0%, 100%)              pure white */
const SAFE_LIGHT_ACCENT         = '#446a55';    /* hsl(146.84, 21.84%, 34.12%)   green */
const WHITE                     = '#fffbf0';    /* hsl(44, 100%, 97.06%)         off white */

const METRIC_QUALITY_LENDING_TEXT = '#f6989a';
const METRIC_QCT_PRESENCE_TEXT    = '#efd661';
const METRIC_BRANCH_DENSITY_TEXT  = '#92bfa7';
const METRIC_ASSET_SIZE_TEXT      = '#66a7d1';

const METRIC_QUALITY_LENDING      = '#cf686a';
const METRIC_QCT_PRESENCE         = '#e1c335';
const METRIC_BRANCH_DENSITY       = '#629a7c';
const METRIC_ASSET_SIZE           = '#506e9a';

const YELLOW = '#ffff00';
const BLUE   = '#0000ff';
const GREEN = '#00ff00';
const DARK_GREEN = '#009900';

export const myTheme = {
  name: 'my theme',
  global: {
    colors: {

      // old theme color names for backcompatibility
      brand:                                ACCENT,
      cta:                                  '#d65255',
      qualityLending:                       METRIC_QUALITY_LENDING,
      qctPresence:                          METRIC_QCT_PRESENCE,
      branchDensity:                        METRIC_BRANCH_DENSITY,
      totalAssets:                          METRIC_ASSET_SIZE,
      focus:                                '#ff6e02',
      'accent-1':                           LIGHT_ACCENT, // anim waiting on search results; borders on buttons in popovers for banks
      'accent-2':                           '#00a62d',
      'accent-3':                           '#ff6e02',
      border:                               ACCENT,
      'neutral-1':                          '#5fa818',
      'neutral-2':                          '#005d69',
      'neutral-3':                          '#0c4881',
      'neutral-4':                          '#f0b200',
      text:                                 BLACK,
      'background-back':                    ACCENT,
      'background-front':                   '#5c5c5c',
      lightGrey:                            '#eff2f5',
      midGrey:                              '#a6a7a9',

      // new theme color names
      'pageTextColor':                      BLACK,
      'pageBackgroundColor':                WHITE,
      'footerTextColor':                    SAFE_INVERSE_ACCENT,
      'footerLinkColor':                    SAFE_INVERSE_ACCENT,
      'footerBackgroundColor':              ACCENT,
      'headerTextColor':                    SAFE_INVERSE_ACCENT,
      'headerLinkColor':                    SAFE_INVERSE_ACCENT,
      'headerBackgroundColor':              ACCENT,
      'buttonBackgroundColor':              ACCENT,
      'buttonTextColor':                    WHITE,
      'resultsHeadingBackgroundColor':      BLACK,
      'resultsHeadingTextColor':            WHITE,
      'rangeTrackColor':                    ACCENT,
      'rangeThumbColor':                    BLACK,
      'mapSidePanelButtonBackgroundColor':  BLACK,
      'mapSidePanelButtonTextColor':        WHITE,
      'mapSidePanelBackgroundColor':        WHITE,
      'mapSidePanelTextColor':              BLACK,
      'mapSidePanelToggleBackgroundColor':  WHITE,
      'mapSidePanelToggleTextColor':        BLACK,
      'mapSidePanelFilterBackgroundColor':  WHITE,
      'mapSidePanelFilterTextColor':        BLACK,
      'mapResultHeadingBackgroundColor':    ACCENT,
      'mapResultHeadingTextColor':          WHITE,
      'mapResultBackgroundColor':           WHITE,
      'mapResultTextColor':                 BLUE,
      'mapResultNavigationBackgroundColor': BLACK,
      'mapResultNavigationTextColor':       WHITE,
      'tagBorderColor':                     BLACK,
      'tagBackgroundColor':                 SAFE_INVERSE_ACCENT,
      'tagTextColor':                       BLACK,
      'bankDetailAccentBackgroundColor':    ACCENT,
      'bankDetailAccentTextColor':          WHITE,
      'bankDetailBackgroundColor':          WHITE,
      'bankDetailTextColor':                BLUE,
      'bankDetailAccentColor2':             LIGHT_ACCENT,
      'bankResearchWarningBackgroundColor': BLACK,
      'bankResearchWarningTextColor':       WHITE,
    },
    font: {
      ...fontSans,
      size: '18px',
    },
  },
  button: {
    border: {
      width: '2px',
      radius: '4px',
    },
    padding: 'medium',
    extend: `align-content: center;`,
  },
  anchor: {
    ...fontSans,
  },
  checkBox: {
    check: {
      radius: '8px',
    },
    toggle: {
      radius: '12px',
      size: '24px',
    },
    size: '24px',
  },
  radioButton: {
    size: '12px',
  },
  calendar: {
    small:  { fontSize: '7px',  lineHeight: 1.375, daySize: '13.714285714285714px' },
    medium: { fontSize: '9px',  lineHeight: 1.45,  daySize: '27.428571428571427px' },
    large:  { fontSize: '15px', lineHeight: 1.11,  daySize: '54.857142857142854px' },
  },
  clock: {
    analog: {
      size:   { small: '36px', medium: '48px', large: '72px', xlarge: '108px', huge: '144px' },
      hour:   { width: '4px',   size: '12px' },
      minute: { width: '2px',   size: '6px'  },
      second: { width: '1.5px', size: '5px'  },
    },
    digital: {
      text: {
        xsmall:  { size: '5px',  height: 1.5    },
        small:   { size: '7px',  height: 1.43   },
        medium:  { size: '9px',  height: 1.375  },
        large:   { size: '11px', height: 1.167  },
        xlarge:  { size: '13px', height: 1.1875 },
        xxlarge: { size: '17px', height: 1.125  },
      },
    },
  },
  heading: {
    ...fontSerif,
    level: {
      '1': {
        font: {},
        small:  { size: '34px',  height: '40px',  maxWidth: '816px' },
        medium: { size: '50px',  height: '56px',  maxWidth: '1200px' },
        large:  { size: '82px',  height: '88px',  maxWidth: '1968px' },
        xlarge: { size: '114px', height: '120px', maxWidth: '2736px' },
      },
      '2': {
        font: {},
        small:  { size: '26px', height: '32px', maxWidth: '624px' },
        medium: { size: '34px', height: '40px', maxWidth: '816px' },
        large:  { size: '50px', height: '56px', maxWidth: '1200px' },
        xlarge: { size: '66px', height: '72px', maxWidth: '1584px' },
      },
      '3': {
        font: {},
        small:  { size: '22px', height: '28px', maxWidth: '528px' },
        medium: { size: '26px', height: '32px', maxWidth: '624px' },
        large:  { size: '34px', height: '40px', maxWidth: '816px' },
        xlarge: { size: '42px', height: '48px', maxWidth: '1008px' },
      },
      '4': {
        font: {},
        small:  { size: '18px', height: '24px', maxWidth: '432px' },
        medium: { size: '18px', height: '24px', maxWidth: '432px' },
        large:  { size: '18px', height: '24px', maxWidth: '432px' },
        xlarge: { size: '18px', height: '24px', maxWidth: '432px' },
      },
      '5': {
        font: {},
        small:  { size: '16px', height: '22px', maxWidth: '384px' },
        medium: { size: '16px', height: '22px', maxWidth: '384px' },
        large:  { size: '16px', height: '22px', maxWidth: '384px' },
        xlarge: { size: '16px', height: '22px', maxWidth: '384px' },
      },
      '6': {
        font: {},
        small:  { size: '14px', height: '20px', maxWidth: '336px' },
        medium: { size: '14px', height: '20px', maxWidth: '336px' },
        large:  { size: '14px', height: '20px', maxWidth: '336px' },
        xlarge: { size: '14px', height: '20px', maxWidth: '336px' },
      },
    },
    responsiveBreakpoint: 'small',
    weight: 600,
  },
  paragraph: {
    small:   { size: '14px', height: '20px', maxWidth: '336px' },
    medium:  { size: '18px', height: '24px', maxWidth: '432px' },
    large:   { size: '22px', height: '28px', maxWidth: '528px' },
    xlarge:  { size: '26px', height: '32px', maxWidth: '624px' },
    xxlarge: { size: '34px', height: '40px', maxWidth: '816px' },
  },
  text: {
    ...fontSans,
    xsmall:  { size: '12px', height: '18px', maxWidth: '288px' },
    small:   { size: '14px', height: '20px', maxWidth: '336px' },
    medium:  { size: '18px', height: '24px', maxWidth: '432px' },
    large:   { size: '22px', height: '28px', maxWidth: '528px' },
    xlarge:  { size: '26px', height: '32px', maxWidth: '624px' },
    xxlarge: { size: '34px', height: '40px', maxWidth: '816px' },
  },
  rangeInput: {
    thumb: {
      color: 'rangeThumbColor',
    },
    track: {
      color: 'rangeTrackColor',
    },
  },
  textInput: {
    extend: (props) => props.inputStyle,
  },
};

export const myCustomTheme = deepMerge(grommet, myTheme);
